import { HotelRoom, HotelFeePolicy, HotelMarkupContext } from "@/lib/redux/features/hotel/type"
import { getHotelDisplayPrice } from "@/utils/hotel/hotelFeeMarkup"

type PerNightRate = Pick<HotelRoom["perRoomPerNightRate"], "exclusive" | "tax" | "fees">

const getRoomNights = (context: HotelMarkupContext) =>
  Math.max(1, context.nights)

const getRoomCount = (context: HotelMarkupContext) =>
  Math.max(1, context.roomsNum)

/** Booking exclusive base: perRoomPerNightRate.exclusive × nights × roomsNum (markup applies here only). */
export const getHotelExclusiveTotal = (
  perRoomPerNightRate: Pick<PerNightRate, "exclusive">,
  context: HotelMarkupContext,
) =>
  perRoomPerNightRate.exclusive * getRoomNights(context) * getRoomCount(context)

/** Tax + fees for full booking (no markup). */
export const getHotelTaxFeesTotal = (
  perRoomPerNightRate: Pick<PerNightRate, "tax" | "fees">,
  context: HotelMarkupContext,
) =>
  (perRoomPerNightRate.tax + perRoomPerNightRate.fees) * getRoomNights(context) * getRoomCount(context)

/** Per room per night after markup: (exclusive with policy) + tax + fees. */
export const getHotelMarkedUpPerNightTotal = (
  perRoomPerNightRate: PerNightRate,
  policy: HotelFeePolicy | null | undefined,
  context: HotelMarkupContext,
) => {
  const { exclusive, tax, fees } = perRoomPerNightRate
  const taxFees = tax + fees
  if (!policy) return exclusive + taxFees
  return getHotelDisplayPrice(exclusive, policy, context) + taxFees
}

/** totalAmount = (exclusive×markup + tax + fees) × nights × roomsNum */
export const getHotelBookingTotalAmount = (
  perRoomPerNightRate: PerNightRate,
  policy: HotelFeePolicy | null | undefined,
  context: HotelMarkupContext,
) =>
  getHotelMarkedUpPerNightTotal(perRoomPerNightRate, policy, context)
  * getRoomNights(context)
  * getRoomCount(context)

/** Supplier rate per night from room search API (exclusive + tax + fees). */
export const getRoomBasePerNightPrice = (room: Pick<HotelRoom, "perRoomPerNightRate">) =>
  room.perRoomPerNightRate.exclusive +
  room.perRoomPerNightRate.tax +
  room.perRoomPerNightRate.fees

/** Tax and fees per night — not included in markup base. */
export const getRoomTaxFeesPerNight = (room: Pick<HotelRoom, "perRoomPerNightRate">) =>
  room.perRoomPerNightRate.tax + room.perRoomPerNightRate.fees

/** Per-night price: (exclusive with markup) + tax + fees. */
export const getRoomPerNightPrice = (
  room: Pick<HotelRoom, "perRoomPerNightRate">,
  policy?: HotelFeePolicy | null,
  context?: HotelMarkupContext,
) => {
  if (!policy || !context) {
    return getRoomBasePerNightPrice(room)
  }
  return getHotelMarkedUpPerNightTotal(room.perRoomPerNightRate, policy, context)
}

export const getRoomBaseTotalPayment = (room: Pick<HotelRoom, "totalPayment">) =>
  room.totalPayment.exclusive +
  room.totalPayment.tax +
  room.totalPayment.fees

/** Total payment from per-night rate: markup on exclusive only, then tax + fees. */
export const getRoomTotalPayment = (
  room: Pick<HotelRoom, "perRoomPerNightRate" | "totalPayment">,
  policy?: HotelFeePolicy | null,
  context?: HotelMarkupContext,
) => {
  if (!context) {
    const { exclusive, tax, fees } = room.totalPayment
    return exclusive + tax + fees
  }
  return getHotelBookingTotalAmount(room.perRoomPerNightRate, policy, context)
}

export const getRoomPerNightDisplayPrice = (
  room: Pick<HotelRoom, "perRoomPerNightRate">,
  policy: HotelFeePolicy | null | undefined,
  context: HotelMarkupContext,
) => getRoomPerNightPrice(room, policy, context)

export const getRoomTotalDisplayPayment = (
  room: Pick<HotelRoom, "perRoomPerNightRate" | "totalPayment">,
  policy: HotelFeePolicy | null | undefined,
  context: HotelMarkupContext,
) => getRoomTotalPayment(room, policy, context)
